'use client'

import { nextPlaybackRate } from '@/lib/playbackRate'
import { RefObject, SyntheticEvent, useCallback, useEffect, useState } from 'react'

export interface UsePCVideoPlaybackParams {
  videoRef: RefObject<HTMLVideoElement | null>
}

export interface UsePCVideoPlaybackReturn {
  isPlaying: boolean
  isMuted: boolean
  currentTime: number
  duration: number
  playbackRate: number
  togglePlay: () => void
  toggleMute: () => void
  cyclePlaybackRate: () => void
  seekTo: (time: number) => void
  /** <video> にそのまま渡すイベントハンドラ群。要素が付け替わっても React が再バインドする */
  videoEventHandlers: {
    onPlay: () => void
    onPause: () => void
    onTimeUpdate: (e: SyntheticEvent<HTMLVideoElement>) => void
    onLoadedMetadata: (e: SyntheticEvent<HTMLVideoElement>) => void
    onCanPlayThrough: (e: SyntheticEvent<HTMLVideoElement>) => void
    onDurationChange: (e: SyntheticEvent<HTMLVideoElement>) => void
    onVolumeChange: (e: SyntheticEvent<HTMLVideoElement>) => void
  }
  formatTime: (time: number) => string
}

/**
 * PC モーダルの自前コントロール（再生/停止・ミュート・再生速度・シーク）の状態。
 * 動画個別ページの useVideoPlayer と同じ操作体系だが、<video> の ref は
 * VideoModal（PiP・再開位置の引き継ぎ）が握っているので外から受け取る。
 *
 * 状態は <video> のイベントから同期する（プロパティ直書きの結果を信用しない）。
 * 動画評価画面との切替で <video> が unmount/mount されても、React の props 経由の
 * ハンドラは新しい要素に自動で貼り直されるため、ここで addEventListener はしない。
 */
export function usePCVideoPlayback({ videoRef }: UsePCVideoPlaybackParams): UsePCVideoPlaybackReturn {
  const [isPlaying, setIsPlaying] = useState(true)
  const [isMuted, setIsMuted] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [playbackRate, setPlaybackRate] = useState(1)

  const togglePlay = useCallback(() => {
    const video = videoRef.current
    if (!video) return
    if (video.paused) {
      video.play().catch(() => {
        // 再生がブロックされた場合は pause 状態のまま（中央の再生ボタンで再試行できる）
      })
    } else {
      video.pause()
    }
  }, [videoRef])

  const toggleMute = useCallback(() => {
    const video = videoRef.current
    if (!video) return
    video.muted = !video.muted
    // volumechange が来る前でも UI を即時反映
    setIsMuted(video.muted)
  }, [videoRef])

  const cyclePlaybackRate = useCallback(() => {
    setPlaybackRate((prev) => nextPlaybackRate(prev))
  }, [])

  const seekTo = useCallback(
    (time: number) => {
      const video = videoRef.current
      if (!video) return
      video.currentTime = time
      setCurrentTime(time)
    },
    [videoRef],
  )

  // 速度変更を <video> へ反映（要素は VideoModal が保持。動画差し替え時は loadedmetadata 側で再適用）
  useEffect(() => {
    const video = videoRef.current
    if (!video) return
    video.playbackRate = playbackRate
  }, [videoRef, playbackRate])

  const syncDuration = useCallback((video: HTMLVideoElement) => {
    if (Number.isFinite(video.duration)) setDuration(video.duration)
  }, [])

  const onPlay = useCallback(() => setIsPlaying(true), [])
  const onPause = useCallback(() => setIsPlaying(false), [])
  const onTimeUpdate = useCallback((e: SyntheticEvent<HTMLVideoElement>) => {
    setCurrentTime(e.currentTarget.currentTime)
  }, [])
  const onLoadedMetadata = useCallback(
    (e: SyntheticEvent<HTMLVideoElement>) => {
      const video = e.currentTarget
      // 別動画へ切り替えたとき・評価画面から戻って要素が作り直されたときに速度を引き継ぐ
      video.playbackRate = playbackRate
      setCurrentTime(video.currentTime)
      syncDuration(video)
    },
    [playbackRate, syncDuration],
  )
  // 初期値は「再生中」（autoPlay 前提）だが、自動再生がブロックされた場合や、
  // アンケートゲートが再生前に pause() した場合は play/pause イベントが一度も来ず
  // 「止まっているのに中央の再生ボタンが出ない」状態になる。
  // 仕様上、自動再生の試行は HAVE_ENOUGH_DATA 到達時（canplaythrough 発火の直前）に
  // 行われるので、この時点の paused を正とみなして同期する。
  const onCanPlayThrough = useCallback((e: SyntheticEvent<HTMLVideoElement>) => {
    setIsPlaying(!e.currentTarget.paused)
  }, [])
  const onDurationChange = useCallback(
    (e: SyntheticEvent<HTMLVideoElement>) => syncDuration(e.currentTarget),
    [syncDuration],
  )
  const onVolumeChange = useCallback((e: SyntheticEvent<HTMLVideoElement>) => {
    setIsMuted(e.currentTarget.muted)
  }, [])

  const formatTime = useCallback((time: number) => {
    const safe = Number.isFinite(time) && time > 0 ? time : 0
    const minutes = Math.floor(safe / 60)
    const seconds = Math.floor(safe % 60)
    return `${minutes}:${seconds.toString().padStart(2, '0')}`
  }, [])

  return {
    isPlaying,
    isMuted,
    currentTime,
    duration,
    playbackRate,
    togglePlay,
    toggleMute,
    cyclePlaybackRate,
    seekTo,
    videoEventHandlers: {
      onPlay,
      onPause,
      onTimeUpdate,
      onLoadedMetadata,
      onCanPlayThrough,
      onDurationChange,
      onVolumeChange,
    },
    formatTime,
  }
}
